import Col from 'antd/lib/col';
import Row from 'antd/lib/row';
import classNames from 'classnames';
import moment from 'moment';
import * as React from 'react';
import withStyles from 'react-jss';
import { SEFFBid } from 'src/dto/response/fetchSEBids';
import { StylesProps } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import { ChargeListCardsStyles } from '../../../DiscoverRate/ChargeListCards.styles';
import { numberFormatted } from 'src/utils/utils';
import { SEFFBidCardStyles } from './SEFFBidCard.styles';
import { stops } from 'src/Library/Helper';
import Tooltip from 'antd/lib/tooltip';

const styles = (theme) => ({
  ...ChargeListCardsStyles (theme),
  ...SEFFBidCardStyles(theme),
})

interface IProps extends StylesProps<ReturnType<typeof styles>> {
  searchResult: SEFFBid;
  deviationFromL1: number;
  extraContent: {
    isServiceNamePresent: boolean;
    isCutOffPresent: boolean;
  };
  onViewDetails: (bidId: string) => void;
}

interface StateProps {
  imgDimensions: {
    height: number,
    width: number,
  };
  isViewDetailsVisible: boolean;
};

const VesselSchedulesCards = (props : IProps): React.ReactElement => {

  const [state, setState] = useGenericState<StateProps>({
    imgDimensions: {
      height: 0,
      width: 0,
    },
    isViewDetailsVisible: false,
  });

  const {
    classes,
    searchResult,
    onViewDetails,
    deviationFromL1,
  } = props;

  const {
    imgDimensions,
    isViewDetailsVisible,
  } = state;

  React.useEffect( () => {
    getImgHeightWidth();
  }, [searchResult?.logoUrlToShow]);

  const getImgHeightWidth = () => {
    const img = new Image();
    img.src = searchResult?.logoUrlToShow;
    const maxWidth = 100;
    const maxHeight = 40;
    img.onload = () => {
      const imgHeight = img.naturalHeight || img.height;
      const imgWidth = img.naturalWidth || img.width;
      const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
      const width = imgWidth * ratio;
      const height = imgHeight * ratio;
      setState({ imgDimensions: { width, height } });
    };
  };

  const renderCarrierInfo = () : React.ReactElement => {
    return (
      <Col span={5} className={classes.carrierInfo}>
        <span style={{ width: '100px', marginLeft: 20, marginRight: 30, minWidth: '100px' }}>
          <img
            src={searchResult?.logoUrlToShow}
            className={classes.carrierLogo}
            style={{ ...imgDimensions }}
          />
        </span>
      </Col>
    );
  };

  const renderRouteInfo = (): React.ReactElement => {
    return (
      <>
        <Col span={4} className={classNames(classes.cardBodyColumn, classes.columnBorderLeft)}>
          <Tooltip title={searchResult?.originPortName}>
            <span className={classes.value}>
              {searchResult?.originPortName}
            </span>
          </Tooltip>
          
          <span className={classes.label}>
            POL
          </span>
        </Col>
        <Col span={4} className={classes.cardBodyColumn}>
          <span className={classes.value}>
            {searchResult?.validityStartDate && moment(searchResult?.validityStartDate).format('DD MMM YYYY')}
          </span>
          <span className={classes.label}>
            Starting Date
          </span>
        </Col>
        <Col span={4} className={classes.cardBodyColumn}>
          <span className={classes.value}>
            {searchResult?.validityEndDate && moment(searchResult?.validityEndDate).format('DD MMM YYYY')}
          </span>
          <span className={classes.label}>
            End Date
          </span>
        </Col>
      </>
    );
  };

  const renderRates = () : React.ReactElement => {
    return (
      <Col span={7} className={classNames(classes.cardBodyColumn, classes.columnBorderLeft)}>
        <span className={classes.boldPrice}>
          {searchResult?.chargeForRanking?.currency} {numberFormatted(searchResult?.chargeForRanking?.amount, {
            places: 2,
            thousandSeparated: true,
          })}
        </span>
        <span className={classes.label}>
          {searchResult?.chargeForRanking?.name}
        </span>
        {searchResult?.rank !== 1 && deviationFromL1 && (
          <span>
            <b>Deviation from L1</b> {searchResult?.chargeForRanking?.currency} {numberFormatted(deviationFromL1, {
              places: 2,
              thousandSeparated: true,
            })}
          </span>
        )}
      </Col>
    );
  };
  
  const getBottomColor = () => {
    return 'rgba(255, 245, 219, 0.7)';
  };

  const handleViewDetailsClick = () : void => {
    onViewDetails(searchResult.bidId);
  };

  const renderBottomDivContent = () : React.ReactElement => {
    return (
      <Row
        className={classes.bottomDiv}
        onClick={(e) => {
          stops(e);
          handleViewDetailsClick()
        }}
        style={{
          backgroundColor: getBottomColor(),
          border: '0 0 3px 3px',
          cursor: 'pointer',
        }}
      >
        <Col span={3} className={classes.bottomContent} style={{ paddingLeft: '15px' }}>
          Rank {searchResult?.rank}
        </Col>
        <Col span={3} className={classes.bottomContent} style={{ paddingLeft: '10px' }}>
          FF : {searchResult?.ffName}
        </Col>
        {<Col span={3} className={classes.bottomContent} style={{ paddingLeft: '10px' }}>
          {searchResult.mode ===  'FCL' ? `Carrier : ${searchResult?.carrierName}` : null}
        </Col>}
        {searchResult?.incoterm ? <Col span={3} className={classes.bottomContent} style={{ paddingLeft: '15px' }}>
          Incoterm {searchResult?.incoterm}
        </Col> : null}
        {searchResult?.containerSize ? <Col span={3} className={classes.bottomContent} style={{ paddingLeft: '15px' }}>
          Container Size {searchResult?.containerSize}
        </Col> : null}
        {searchResult?.containerType ? <Col span={4} className={classes.bottomContent} style={{ paddingLeft: '15px' }}>
          Container Type {searchResult?.containerType}
        </Col> : null}
        {<Col span={3} className={classes.bottomContent} style={{ paddingLeft: '15px' }}>
          {searchResult.mode !== 'AIR' && searchResult?.transitDays ? `Transit Days ${searchResult?.transitDays}` : null}
        </Col>}
        <Col span={3} className={classes.bottomContent}>
          <span style={{ color: '#006EC3', paddingRight: '15px' }}>
            {isViewDetailsVisible ? 'Hide Details' : 'View Details'}
          </span>
        </Col>
      </Row>
    );
  };

  return (
    <div className={classes.cardDiv} style={{ marginBottom: '9px', width: 770 }}>
      <Row className={classes.cardBody}>
        {renderCarrierInfo()}
        {renderRouteInfo()}
        {renderRates()}
      </Row>
      {renderBottomDivContent()}
    </div>
  );
};

export default React.memo(withStyles(styles)(VesselSchedulesCards));
